import { Divider, Input, Select } from "antd";
import { CheckOutlined, SearchOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";

import "./OperationMultiSelect.scss";

const OperationMultiSelect = ({
  label,
  placeholder,
  listOptions = [],
  value = [],
  onChange,
  width = 200,
  dropdownWidth = 280,
  disabled = false
}) => {
  const [searchText, setSearchText] = useState("");

  // Filter options based on search text
  const filteredOptions = useMemo(() => {
    if (!searchText) return listOptions;
    return listOptions.filter(
      (option) =>
        option.label?.toLowerCase().includes(searchText.toLowerCase()) ||
        option.value?.toString().toLowerCase().includes(searchText.toLowerCase())
    );
  }, [listOptions, searchText]);

  // Handle select all visible options
  const handleSelectAll = () => {
    const allVisibleValues = filteredOptions.map((opt) => opt.value);
    const newValues = [...new Set([...value, ...allVisibleValues])];
    onChange?.(newValues);
  };

  // Handle clear all selections
  const handleClearAll = () => {
    onChange?.([]);
  };

  // Handle individual option change
  const handleOptionChange = (optionValue, checked) => {
    if (checked) {
      onChange?.([...value, optionValue]);
    } else {
      onChange?.(value.filter((v) => v !== optionValue));
    }
  };

  // Custom dropdown render
  const dropdownRender = () => (
    <div className="operation-multi-select-dropdown">
      {/* Search input */}
      <div className="dropdown-search">
        <Input
          placeholder="検索..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      <Divider style={{ margin: "8px 0" }} />

      {/* Options list with checkboxes */}
      <div className="dropdown-options">
        {filteredOptions.length === 0 ? (
          <div className="dropdown-empty">データがありません</div>
        ) : (
          filteredOptions.map((option) => {
            const isChecked = value.includes(option.value);
            return (
              <div
                key={option.value}
                className={`dropdown-option ${isChecked ? "selected" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOptionChange(option.value, !isChecked);
                }}
              >
                <span className={`custom-checkbox ${isChecked ? "checked" : ""}`}>
                  {isChecked && <CheckOutlined className="checkmark" />}
                </span>
                <span className="option-label">{option.label}</span>
              </div>
            );
          })
        )}
      </div>

      <Divider style={{ margin: "8px 0" }} />

      {/* Action buttons */}
      <div className="dropdown-actions">
        <span className="action-btn" onClick={handleClearAll}>
          選択をクリア
        </span>
        <span className="action-btn" onClick={handleSelectAll}>
          全選択
        </span>
      </div>
    </div>
  );

  return (
    <div className="operation-multi-select">
      {label && <span className="select-label">{label}</span>}
      <Select
        mode="multiple"
        style={{ width }}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        maxTagCount={1}
        maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
        dropdownRender={dropdownRender}
        dropdownStyle={{ padding: 0, minWidth: dropdownWidth }}
        popupMatchSelectWidth={false}
        showSearch={false}
        open={undefined}
        listHeight={0}
        virtual={false}
        popupClassName="operation-multi-select-popup"
      >
        {listOptions.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};

OperationMultiSelect.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  listOptions: PropTypes.array,
  value: PropTypes.array,
  onChange: PropTypes.func,
  width: PropTypes.number,
  dropdownWidth: PropTypes.number,
  disabled: PropTypes.bool
};

export default OperationMultiSelect;
